import { clone } from '@jkhy/frontend-utils/functions';
import { AccountModelDto, AchCompanyModelDto, AchProductModelDto } from '@treasury/api/bo';
import { AchCompany } from '@treasury/domain/company/entities/ach-company.entity';
import { FeatureApiType } from '../../types/product-feature.enum';
import { CompanyProductFeature } from './company-product-feature.entity';

const defaultDto: AchProductModelDto = {
    id: 0,
    productUniqueId: '',
    type: FeatureApiType.ACH,
    isConfigurable: true,
    dailyLimit: 0,
    internationalTemplateApprovalCount: 'None',
    paymentApprovalCount: 'None',
    recipientApprovalCount: 'None',
    batchApprovalCount: 'None',
    disableSameDay: false,
    validateAccountBalance: false,
    accountBalanceValidationConfiguredInCore: false,
    achCompanies: [],
    achCompanyMatchType: 0,
    paymentProcessingCompanyConfiguredInCore: false,
    allowSameDayAch: false,
    allowStateTaxPayments: false,
    allowFederalTaxPayments: false,
    allowTaxPayments: false,
    allowChildSupportPayments: false,
    allowInternationalAchPayments: false,
    allowUnbalancedPayments: false,
    allowOnUsAccessManagement: false,
    allowAchNotificationOfChange: false,
    allowReturnReview: false,
};

export class AchProductFeature extends CompanyProductFeature<AchProductModelDto> {
    constructor(dto: AchProductModelDto = clone(defaultDto)) {
        super(dto);
    }

    public get dailyLimit() {
        return this.dto.dailyLimit;
    }

    public set dailyLimit(value: number) {
        this.dto.dailyLimit = value;
    }

    public get internationalTemplateApprovalCount() {
        if (this.dto.internationalTemplateApprovalCount === 'None') {
            return 0;
        }
        if (this.dto.internationalTemplateApprovalCount === undefined) {
            return undefined;
        }
        return Number(this.dto.internationalTemplateApprovalCount);
    }

    public set internationalTemplateApprovalCount(value: number | undefined) {
        this.dto.internationalTemplateApprovalCount = value === 0 ? 'None' : `${value}`;
    }

    public get paymentApprovalCount() {
        if (this.dto.paymentApprovalCount === 'None') {
            return 0;
        }
        if (this.dto.paymentApprovalCount === undefined) {
            return undefined;
        }
        return Number(this.dto.paymentApprovalCount);
    }

    public set paymentApprovalCount(value: number | undefined) {
        this.dto.paymentApprovalCount = value === 0 ? 'None' : `${value}`;
    }

    public get recipientApprovalCount() {
        if (this.dto.recipientApprovalCount === 'None') {
            return 0;
        }
        if (this.dto.recipientApprovalCount === undefined) {
            return undefined;
        }
        return Number(this.dto.recipientApprovalCount);
    }

    public set recipientApprovalCount(value: number | undefined) {
        this.dto.recipientApprovalCount = value === 0 ? 'None' : `${value}`;
    }

    public get batchApprovalCount() {
        if (this.dto.batchApprovalCount === 'None') {
            return 0;
        }
        if (this.dto.batchApprovalCount === undefined) {
            return undefined;
        }
        return Number(this.dto.batchApprovalCount);
    }

    public set batchApprovalCount(value: number | undefined) {
        this.dto.batchApprovalCount = value === 0 ? 'None' : `${value}`;
    }

    public get allowSameDayAch() {
        return this.dto.allowSameDayAch ?? false;
    }

    public set allowSameDayAch(value: boolean) {
        this.dto.allowSameDayAch = value;
    }

    public get disableSameDay() {
        return this.dto.disableSameDay ?? false;
    }

    public set disableSameDay(value: boolean) {
        this.dto.disableSameDay = value;
    }

    public get validateAccountBalance() {
        return this.dto.validateAccountBalance ?? false;
    }

    public set validateAccountBalance(value: boolean) {
        this.dto.validateAccountBalance = value;
    }

    public get accountBalanceValidationConfiguredInCore() {
        return this.dto.accountBalanceValidationConfiguredInCore ?? false;
    }

    public set accountBalanceValidationConfiguredInCore(value: boolean) {
        this.dto.accountBalanceValidationConfiguredInCore = value;
    }

    public get achCompanies() {
        return this.dto.achCompanies?.map(company => new AchCompany(company)) ?? [];
    }

    public set achCompanies(achCompanies: AchCompany[] | undefined) {
        this.dto.achCompanies =
            achCompanies?.map(company => company.toDto() as AchCompanyModelDto) ?? [];
    }

    public get achCompanyMatchType() {
        return this.dto.achCompanyMatchType;
    }

    public set achCompanyMatchType(value: number) {
        this.dto.achCompanyMatchType = value;
    }

    public get paymentProcessingCompanyConfiguredInCore() {
        return this.dto.paymentProcessingCompanyConfiguredInCore ?? false;
    }

    public set paymentProcessingCompanyConfiguredInCore(value: boolean) {
        this.dto.paymentProcessingCompanyConfiguredInCore = value;
    }

    public get allowStateTaxPayments() {
        return this.dto.allowStateTaxPayments ?? false;
    }

    public set allowStateTaxPayments(value: boolean) {
        this.dto.allowStateTaxPayments = value;
    }

    public get allowFederalTaxPayments() {
        return this.dto.allowFederalTaxPayments ?? false;
    }

    public set allowFederalTaxPayments(value: boolean) {
        this.dto.allowFederalTaxPayments = value;
    }

    public get allowTaxPayments() {
        return this.dto.allowTaxPayments ?? false;
    }

    public set allowTaxPayments(value: boolean) {
        this.dto.allowTaxPayments = value;
    }

    public get allowChildSupportPayments() {
        return this.dto.allowChildSupportPayments ?? false;
    }

    public set allowChildSupportPayments(value: boolean) {
        this.dto.allowChildSupportPayments = value;
    }

    public get allowInternationalAchPayments() {
        return this.dto.allowInternationalAchPayments ?? false;
    }

    public set allowInternationalAchPayments(value: boolean) {
        this.dto.allowInternationalAchPayments = value;
    }

    public get allowUnbalancedPayments() {
        return this.dto.allowUnbalancedPayments ?? false;
    }

    public set allowUnbalancedPayments(value: boolean) {
        this.dto.allowUnbalancedPayments = value;
    }

    public get allowOnUsAccessManagement() {
        return this.dto.allowOnUsAccessManagement ?? false;
    }

    public set allowOnUsAccessManagement(value: boolean) {
        this.dto.allowOnUsAccessManagement = value;
    }

    public get allowAchNotificationOfChange() {
        return this.dto.allowAchNotificationOfChange ?? false;
    }

    public set allowAchNotificationOfChange(value: boolean) {
        this.dto.allowAchNotificationOfChange = value;
    }

    public get allowReturnReview() {
        return this.dto.allowReturnReview ?? false;
    }

    public set allowReturnReview(value: boolean) {
        this.dto.allowReturnReview = value;
    }

    /**
     * This getter is not used in the UI, but it is required for the DTO to be valid to be used in the TransferTable base component.
     */
    public get accounts(): AccountModelDto[] | undefined {
        return [];
    }
}
